import { createRouter, createWebHistory } from "vue-router";
import { fetchUserData } from "@/services/account";
import routes from "./routes";
import posthog from 'posthog-js'

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return { top: 0 };
        }
    }
});

router.beforeEach(async (to, from, next) => {
    if (to.name !== 'login' && to.name !== 'register') {
        document.title = `${to.meta.title} - Tripcart`;
    }
    const APP_URL = import.meta.env.VITE_APP_URL;
    const currentURL = window.location.host;
    const APP_URL_STRIPPED = APP_URL.replace(/^(https?:\/\/)/, '');
    if (to.name == 'NotFound') {
        window.location.href = APP_URL_STRIPPED == currentURL ? '/login' : '/not-found'
    } else {
        if (to.meta.middleware) {
            for (const middleware of to.meta.middleware) {
                await middleware(to, from, next);
            }
        }
        // Check team-management-create route usage
        if (to.name == 'team-management-create') {
            let plansAndUsages = localStorage.getItem('plansAndUsages');
            plansAndUsages = plansAndUsages ? JSON.parse(plansAndUsages) : {};
            if (plansAndUsages) {
                switch (to.name.trim()) {
                case "team-management-create":
                    plansAndUsages.team_used_percentage >= 100 ? next({ name: "team-management" }) : next();
                    break;
                default:
                    break;
                }
            }
        }
        // Refined subscription check
        const canProceed = await checkSubscription(to);
        if (canProceed) {
            next();
        } else {
            next({ name: "plans" });
        }
    }
});

// Automatically track page views
router.afterEach((to) => {
    posthog.capture('$pageview', { current_url: to.fullPath })
})

async function checkSubscription(to) {
    // Excluded routes that don't require subscription/trial check
    const excludedRoutes = [
        "agency-info",
        "website-info",
        "agency-address",
        "my-agencies",
        "login",
        "plans",
        "register",
        "verify-email",
        "forget-password",
        "password-notification",
        "forbidden"
    ];

    // Defensive: if route name is missing, allow navigation
    if (!to.name || typeof to.name !== "string") return true;

    const routeName = to.name.trim();

    // If route is excluded, allow navigation
    if (excludedRoutes.includes(routeName)) {
        return true;
    }

    // Try to get values from localStorage, but if missing, fetch from API
    let isTrialExpired = null;
    let isValidSubscription = null;

    try {
        isTrialExpired = JSON.parse(localStorage.getItem("isTrialExpired"));
    } catch (e) {
        isTrialExpired = null;
    }
    try {
        isValidSubscription = JSON.parse(localStorage.getItem("isValidSubscription"));
    } catch (e) {
        isValidSubscription = null;
    }

    // If values are missing or not boolean, fetch from API
    if (typeof isTrialExpired !== "boolean" || typeof isValidSubscription !== "boolean") {
        try {
            const response = await fetchUserData();
            if (response && response.data && response.data.data) {
                isTrialExpired = !!response.data.data.is_trial_expired;
                isValidSubscription = !!response.data.data.is_valid_subscription;
                localStorage.setItem("isTrialExpired", JSON.stringify(isTrialExpired));
                localStorage.setItem("isValidSubscription", JSON.stringify(isValidSubscription));
            } else {
                // If API fails, allow navigation to avoid lockout
                return true;
            }
        } catch (e) {
            // If API fails, allow navigation to avoid lockout
            return true;
        }
    }

    // If trial is expired and not subscribed, block navigation
    if (isTrialExpired && !isValidSubscription) {
        return false;
    }

    // Otherwise, allow navigation
    return true;
}

export default router;
